import React, { useCallback } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import imageUrlBuilder from '@sanity/image-url';

import { IconButton } from '@tmap/mmm-style-guide/src/IconButton';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import {
  Share,
  Info,
} from '@tmap/mmm-style-guide/src/Icon';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { Chip } from '@tmap/mmm-style-guide/src/Chip';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Tooltip } from '@tmap/mmm-style-guide/src/Tooltip';

import isEmptyish from '@tmap/mmm-core/isEmptyish';
import { UserListItemStatus } from '@tmap/mmm-core/commonApp/userListItemStatus';
import { toKey, types } from '@tmap/mmm-core/entityKey';
import locationLabelText from '@tmap/mmm-core/locationLabelText';

import ColorBlocks from '../../layouts/colorBlocks';
import Metatags from '../../components/metatags';
import ClaimDialog from '../../components/offers/claimDialog';
import OfferValueItem from '../../components/offers/offerValueItem';
import SanityGallery from '../../components/fullscreenGallery';
import IncentiveCard from '../../components/incentiveCard';
import ApprovalProcessBlock from '../../components/offers/approvalProcess';
import OfferBody from '../../components/offers/offerBody';
import ResponsiveBottomDrawer from '../../components/responsiveBottomDrawer';
import FavoriteButton from '../../components/favoriteButton';

import useUserListItem from '../../hooks/useUserListItem';

import isCommonAppIncentive from '../../lib/incentive/isCommonAppIncentive';
import getPromotedIncentives from '../../lib/incentive/getPromotedIncentives';
import isWaitlist from '../../lib/incentive/isWaitlist';
import analytics from '../../lib/analytics';
import { LIGHT_THEME } from '../../lib/themes';
import { getTagPills } from '../../lib/tags';

import client from '../../client';
import setCacheHeaders from '../../lib/setCacheHeaders';
import ExternalApplyButton from '../../components/offers/externalApplyButton';
import IncentiveCTAButton from '../../components/incentiveCard/incentiveCTAButton';
import OfferProgressContext from '../../components/incentiveCard/offerProgressContext';
import IncompleteTasksCTA from '../../components/incentiveCard/incompleteTasksCTA';

const builder = imageUrlBuilder(client);

const DynamicShareButton = dynamic(() => import('../../components/shareButton'));

const CircleIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.grey[400]}`,
}));

function IncentiveDetails(props) {
  const { incentive, setClaimDialogOpen } = props;
  const router = useRouter();
  const { requestedApp = false } = router.query;

  let statusHelperText;
  if (incentive.status === 'inactive') {
    statusHelperText = `${incentive.locationText} is no longer accepting applications at this time.`;
  } else if (!isCommonAppIncentive(incentive)) {
    statusHelperText = requestedApp
      ? `We've passed your interest along to ${incentive.locationText}, and you can expect an email shortly.`
      : `We'll help you get in touch with ${incentive.locationText}'s incentive program.`;
  }

  const trackApplyClick = useCallback(() => {
    analytics.track('claim', {
      entityKey: toKey('incentive', incentive._id),
    });
  }, [incentive._id]);

  const handleClaimClick = () => {
    setClaimDialogOpen(true);
    trackApplyClick();
  };

  return (
    <ResponsiveBottomDrawer>
      <Grid container justifyContent='center'>
        <Grid item xs={12}>
          {incentive.status !== 'inactive' && !isCommonAppIncentive(incentive) ? (
            <ExternalApplyButton
              incentive={incentive}
              handleClick={handleClaimClick}
              requested={requestedApp}
            />
          ) : (
            <>
              <IncompleteTasksCTA incentive={incentive} sx={{ mb: 2 }} />
              <IncentiveCTAButton
                incentive={incentive}
                handleClick={trackApplyClick}
                variant='contained'
                color='inverted'
                size='large'
                disableElevation={false}
                fullWidth
                disableWhenInactive
              />
            </>
          )}
        </Grid>
        {isWaitlist(incentive)
          ? (
            <Grid item text='center' sx={{ paddingTop: 1 }}>
              <Info
                sx={{
                  color: (theme) => theme.palette.info.light, fontSize: '16px', marginBottom: -0.25, marginRight: 0.5,
                }}
              />
              <Typography variant='body2' color='textSecondary' component='span'>
                Waitlisted program &mdash; incentive fulfillment is currently paused or delayed.
              </Typography>
            </Grid>
          )
          : (statusHelperText && (
            <Grid item xs={12} text='center' sx={{ paddingTop: 1 }}>
              <Typography variant='body2' color='textSecondary'>
                {statusHelperText}
              </Typography>
            </Grid>
          ))}
      </Grid>
    </ResponsiveBottomDrawer>
  );
}

function ShareButton(props) {
  const { url, entityKey } = props;
  return (
    <Tooltip title='Share' placement='top'>
      <div>
        <DynamicShareButton Button={CircleIconButton} url={url} entityKey={entityKey}>
          <Share fontSize='small' />
        </DynamicShareButton>
      </div>
    </Tooltip>
  );
}

const SlantOfferGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0, 4),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(0, 12),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(12, 4, 0, 4),
  },
}));

function IncentivePage(props) {
  const {
    incentive,
    otherIncentives,
    pageSettings,
    promotedIncentives,
    offerProgressSettings,
  } = props;

  const canonicalUrl = `${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}/get-paid/${incentive.slug.current}`;
  const entityKey = toKey(types.INCENTIVE, incentive._id);

  const images = [
    ...(incentive.heroImage?.asset ? [incentive.heroImage] : []),
    ...(incentive.incentiveImage?.asset ? [incentive.incentiveImage] : []),
    ...(incentive.galleryImages || []),
  ];
  const getPaid = `Get Paid to Live in ${locationLabelText(incentive.locationText, incentive.regionText)}`;
  const pageh1 = !isEmptyish(incentive.title) ? incentive.title : getPaid;
  const pageTitle = `${getPaid}${!isEmptyish(incentive.title) ? ` - ${incentive.title}` : ''}`;

  const favorite = useUserListItem({ entityKey });
  const isFavorite = favorite !== undefined && favorite.status !== UserListItemStatus.REMOVED;

  return (
    <OfferProgressContext.Provider value={offerProgressSettings}>
      <Metatags
        title={pageTitle}
        description={['Find out why people are moving to ',
          locationLabelText(incentive.locationText, incentive.regionText),
          '. Remote workers, and now teachers, nurses and more are eligible for incentives, signing bonuses and other programming.',
        ].join('')}
        canonical={canonicalUrl}
        image={builder.image(images[0]).url()}
      />
      <ColorBlocks footerTheme={LIGHT_THEME}>

        <Grid container sx={{ marginTop: { xs: 2, lg: -2 } }}>
          <Grid item xs={12} lg={6}>
            <SanityGallery mode='slant' mediaList={images} title={locationLabelText(incentive.locationText, incentive.regionText)} />
          </Grid>
          <SlantOfferGrid item xs={12} lg={6}>

            <Grid container sx={{ maxWidth: { xs: 'none', lg: '600px' }, marginBottom: 1 }}>
              <Grid item xs={12}>
                <Grid container alignItems='center' spacing={2}>
                  <Grid item xs>
                    <Grid container spacing={1}>
                      {incentive.tags && getTagPills(incentive.tags).map(
                        ({ label, icon, iconColor }, i) => (
                          <Grid item key={`pill-${i}`}>
                            <Chip variant='pill' size='small' label={label} icon={icon} iconColor={iconColor} />
                          </Grid>
                        ),
                      )}
                      {incentive.communities?.length > 0
                        ? Array.from(
                          new Set(incentive.communities.map((c) => c.regionText)),
                        ).map((regionText, i) => (
                          <Grid item key={`community-${i}`}>
                            <Chip
                              variant='pill'
                              size='small'
                              label={regionText || 'pls add {..., communities[]->{regionText}}'}
                            />
                          </Grid>
                        ))
                        : (
                          <Grid item>
                            <Chip
                              variant='pill'
                              size='small'
                              label={incentive.locationText}
                            />
                          </Grid>
                        )}
                    </Grid>
                  </Grid>
                  <Grid item>
                    <ShareButton url={canonicalUrl} entityKey={entityKey} />
                  </Grid>
                  <Grid item>
                    <div>
                      <FavoriteButton
                        entity={incentive}
                        fontSize='small'
                        color='inverted'
                        Button={CircleIconButton}
                        tooltip={isFavorite ? 'Un-Save This Program' : 'Save This Program'}
                      />
                    </div>
                  </Grid>
                </Grid>

              </Grid>
              <Grid item xs={12} lg={8} sx={{ marginTop: 4, marginBottom: 4 }}>
                <Typography component='h1' fontWeight={600} sx={{ fontSize: { xs: '22px', lg: '32px' } }}>
                  {pageh1}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {(incentive.values || []).map(
                    (v, i) => (
                      <Grid item xs={12} key={i}>
                        <OfferValueItem offerValue={v} />
                      </Grid>
                    ),
                  )}
                </Grid>
              </Grid>
              <Grid item xs sx={{ marginTop: 3, marginBottom: 4 }}>
                <Typography variant='h6' fontWeight={600}>Total Incentive</Typography>
              </Grid>
              <Grid item sx={{ marginTop: 3, marginBottom: 4 }}>
                <Typography variant='h6' fontWeight={600}>
                  {incentive.totalValue ? ['$', new Intl.NumberFormat().format(incentive.totalValue)].join('') : ''}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ClaimDialog incentive={incentive}>
                  {(setClaimDialogOpen) => (
                    <IncentiveDetails
                      incentive={incentive}
                      setClaimDialogOpen={setClaimDialogOpen}
                    />
                  )}
                </ClaimDialog>
              </Grid>
            </Grid>
          </SlantOfferGrid>
        </Grid>

        <ColorBlocks.Block overflow='md'>
          <OfferBody incentive={incentive} />
        </ColorBlocks.Block>

        {isCommonAppIncentive(incentive) && !incentive.disableApprovalProcessBlock && (
          <ColorBlocks.Block color='dim' spacing='large'>
            <ApprovalProcessBlock pageSettings={pageSettings} />
          </ColorBlocks.Block>
        )}
        {(!isCommonAppIncentive(incentive) || promotedIncentives.length > 0) && (
          <ColorBlocks.Block color='dark'>
            <ColorBlocks.Header
              buttonText='Explore Programs'
              href={[
                '/get-paid',
                (incentive.regionType ? `?rt=${incentive.regionType}` : ''),
              ].join('')}
            >
              Related Programs
            </ColorBlocks.Header>
            <Grid container spacing={4}>
              {[...promotedIncentives, ...otherIncentives].slice(0, 3).map((incent, i) => (
                <Grid item xs={12} md={4} key={`incCard-${incent._id}`}>
                  <IncentiveCard
                    incentive={incent}
                    shouldLazyLoad
                    lazyProps={{ offset: 1000 }}
                    placementId={`similarOffersToThis-${i}`}
                    pageId='offer'
                  />
                </Grid>
              ))}
            </Grid>
          </ColorBlocks.Block>
        )}
      </ColorBlocks>
    </OfferProgressContext.Provider>
  );
}

export const getServerSideProps = async (context) => {
  const { slug = '' } = context.query;

  const result = await client.fetch(`
        *[_type == "incentive" && slug.current == $slug][0]{
          ...,
          communities[]->{..., "offers": *[_type == "incentive" && status != "inactive" && ^._id in communities[]._ref]},
          faqs[]{
            _type == 'reference' => @->,
            _type != 'reference' => @,
          }
        }
    `, { slug });

  if (!result) {
    return { notFound: true };
  }

  const [
    otherIncentives = [],
    { pageSettings, offerProgressSettings },
    promotedIncentives = [],
  ] = await Promise.all([
    client.fetch(`
        *[_type == "incentive" && status != "inactive" && regionZone == "${result.regionZone}" && _id != "${result._id}"][0...3]{..., communities[]->{regionText}}
    `),
    client.fetch(`{
      'pageSettings': *[_id == "incentivesSettings"][0],
      'offerProgressSettings': *[_id == "offerProgressSettings"][0],
    }`),
    getPromotedIncentives(4, result.regionZone.toLowerCase()),
  ]);

  const analyticsProps = {
    pageName: 'offer',
    entityKey: toKey(types.INCENTIVE, result._id),
  };

  setCacheHeaders(context.res, 60, 43200);
  return {
    props:
    {
      analyticsProps,
      incentive: result,
      otherIncentives,
      pageSettings,
      promotedIncentives: promotedIncentives.filter((i) => i._id !== result._id),
      offerProgressSettings,
    },
  };
};

export default IncentivePage;
